import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { ReportCard } from '../ReportCard';
import { CreativeThumbLine } from './CreativeCell';
import type { PerformanceReport } from './data/api';
import { compactCurrency, signedPercent } from './data/format';

interface ActionItem {
  key: string;
  content: ReactNode;
}

interface ActionListProps {
  action: string;
  tone: string;
  items: ActionItem[];
  /** Creative rows carry their own thumbnail, so they drop the bullet and indent text lines keep. */
  bare?: boolean;
}

function ActionList({ action, tone, items, bare = false }: ActionListProps): ReactNode {
  return (
    <ReportCard>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: tone }}>{action}</Typography>
        <Chip size="small" label={String(items.length)} />
      </Box>
      {items.length === 0 ? (
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          Nothing flagged this period.
        </Typography>
      ) : (
        <Box
          component="ul"
          sx={{
            m: 0,
            ...(bare ? { p: 0, listStyle: 'none' } : { pl: 3 }),
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {items.map((item) => (
            <Typography
              key={item.key}
              component="li"
              sx={{ fontSize: 11, color: 'text.secondary', minWidth: 0 }}
            >
              {item.content}
            </Typography>
          ))}
        </Box>
      )}
    </ReportCard>
  );
}

export function NextPeriodActions({
  report,
  onOpenCreative,
}: {
  report: PerformanceReport;
  onOpenCreative: (creativeId: string) => void;
}): ReactNode {
  const theme = useTheme();
  const { actions, currencySymbol } = report;

  const tagItems = (tags: PerformanceReport['actions']['scale']): ActionItem[] =>
    tags.map((tag) => ({
      key: JSON.stringify([tag.category, tag.feature, tag.tag]),
      content: `${tag.tag} (${tag.feature}) — ${signedPercent(tag.improvement)} vs avg, ${signedPercent(tag.spendShare).replace('+', '')} of spend`,
    }));
  const creativeItems = (rows: PerformanceReport['actions']['refresh']): ActionItem[] =>
    rows.map((row) => ({
      key: row.assetId,
      content: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <CreativeThumbLine row={row} onOpen={onOpenCreative} />
          </Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>
            {compactCurrency(row.spend, currencySymbol)}
          </Typography>
        </Box>
      ),
    }));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          xl: 'repeat(4, minmax(0, 1fr))',
        },
        gap: 3,
      }}
    >
      <ActionList
        action="Scale"
        tone={theme.palette.success.main}
        items={tagItems(actions.scale)}
      />
      <ActionList
        action="Refresh"
        bare
        tone={theme.palette.primary.main}
        items={creativeItems(actions.refresh)}
      />
      <ActionList
        action="Explore"
        bare
        tone={theme.palette.secondary.main}
        items={creativeItems(actions.explore)}
      />
      <ActionList
        action="Reduce"
        tone={theme.palette.error.main}
        items={tagItems(actions.reduce)}
      />
    </Box>
  );
}
